import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  brandingPoweredByLine,
  loadBrandingLogoDataUrl,
  pdfImageFormat,
  resolveExportBranding,
} from './tenantBranding';

const BRAND = {
  primary: [15, 23, 42],
  accent: [99, 102, 241],
  muted: [100, 116, 139],
  border: [203, 213, 225],
};

function safeFilename(name) {
  return String(name || 'document').replace(/[^\w.-]+/g, '_');
}

function drawPageBorder(doc, pageWidth, pageHeight) {
  const inset = 14;
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.85);
  doc.roundedRect(inset, inset, pageWidth - 2 * inset, pageHeight - 2 * inset, 7, 7, 'S');
}

const DOC_LABELS = {
  quotation: 'Quotation',
  sales_order: 'Sales Order',
  proforma: 'Proforma Invoice',
  invoice: 'Tax Invoice',
};

function docRows(doc) {
  return (doc.items || []).map((i) => {
    const qty = Number(i.quantity) || 0;
    const unitPrice = Number(i.unitPriceTzs) || 0;
    const unitLabel = i.productUnit || 'pcs';
    const name = i.productName || i.brickTypeName || 'Item';
    return {
      product: name,
      qty,
      qtyDisplay: i.productName ? `${qty} ${unitLabel}` : String(qty),
      unit: unitPrice,
      line: Number(i.lineTotalTzs) || qty * unitPrice,
      isShopLine: Boolean(i.productName),
    };
  });
}

export async function exportBrickCustomerDocPdf(doc, options = {}) {
  return exportCustomerDocumentPdf(doc, options);
}

export async function exportCustomerDocumentPdf(doc, options = {}) {
  if (!doc) return;
  const branding = resolveExportBranding(options);
  const poweredBy = brandingPoweredByLine(branding);
  const label = DOC_LABELS[doc.documentType] || 'Document';
  const rows = docRows(doc);
  const total = rows.reduce((s, r) => s + r.line, 0);
  const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const centerX = pageWidth / 2;
  const generatedAt = new Date().toLocaleString('sv-SE', { hour12: false });
  const logoDataUrl = await loadBrandingLogoDataUrl(branding);

  drawPageBorder(pdf, pageWidth, pageHeight);
  pdf.setFillColor(248, 250, 252);
  pdf.roundedRect(margin - 6, margin - 8, pageWidth - margin * 2 + 12, 118, 8, 8, 'F');

  let headerY = margin + 8;
  if (logoDataUrl) {
    const fmt = pdfImageFormat(logoDataUrl);
    pdf.addImage(logoDataUrl, fmt, centerX - 18, headerY, 36, 36);
    headerY += 44;
  }

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...BRAND.accent);
  pdf.text(String(branding.businessName).toUpperCase(), centerX, headerY + 10, { align: 'center' });
  pdf.setFontSize(16);
  pdf.setTextColor(...BRAND.primary);
  pdf.text(label, centerX, headerY + 30, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...BRAND.muted);
  pdf.text(`Generated: ${generatedAt}`, centerX, headerY + 46, { align: 'center' });

  autoTable(pdf, {
    startY: margin + 120,
    body: [
      ['Document no.', doc.documentNumber],
      ['Date', doc.documentDate],
      ['Customer', doc.customerName],
      ['Phone', doc.customerPhone || '—'],
      ['Site / destination', doc.destination || '—'],
      ['Status', String(doc.status || 'draft')],
      ['Total', `TZS ${Math.round(total).toLocaleString()}`],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 5 },
    columnStyles: {
      0: { cellWidth: 120, fontStyle: 'bold', fillColor: [248, 250, 252] },
      1: { cellWidth: pageWidth - margin * 2 - 120 },
    },
    margin: { left: margin, right: margin },
  });

  const shopDoc = rows.some((r) => r.isShopLine);
  autoTable(pdf, {
    startY: (pdf.lastAutoTable?.finalY || margin + 120) + 14,
    head: [[shopDoc ? 'Product' : 'Brick type', shopDoc ? 'Quantity' : 'Qty (pcs)', 'Unit price', 'Line total']],
    body: rows.map((r) => [
      r.product,
      r.qtyDisplay ?? String(r.qty),
      `TZS ${Math.round(r.unit).toLocaleString()}`,
      `TZS ${Math.round(r.line).toLocaleString()}`,
    ]),
    theme: 'grid',
    headStyles: { fillColor: BRAND.accent, textColor: [255, 255, 255] },
    margin: { left: margin, right: margin, bottom: 70 },
  });

  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND.muted);
  const footer =
    doc.documentType === 'invoice'
      ? 'Tax invoice — EFD receipt can be linked when TRA integration is enabled.'
      : `${label} — subject to confirmation before delivery.`;
  pdf.text(footer, margin, pageHeight - 42);
  if (poweredBy) {
    pdf.text(poweredBy, margin, pageHeight - 30);
  }

  pdf.save(`${safeFilename(branding.businessName)}_${safeFilename(doc.documentType)}_${safeFilename(doc.documentNumber)}.pdf`);
}

export function customerDocTypeLabel(type) {
  return DOC_LABELS[type] || type;
}
